// Documents (evidence) API surface. Built on the shared helpers in api/client.ts —
// never raw fetch. One module per resource; DocumentsPage is the only consumer today.
import { request, upload } from './client.js';
import type { DeletionPlan } from './crm.js';

export type SourceType = 'upload' | 'transcript' | 'note' | 'url';
export type DocStatus = 'processing' | 'ready' | 'failed' | 'superseded';
export type TextFormat = 'plain' | 'markdown' | 'transcript';

/** One durable record of "these exact bytes arrived again" (documents.meta.duplicate_uploads). */
export interface DuplicateUpload {
  filename: string;
  at: string;
  by: string | null;
  by_user_id?: number | null;
}

export interface DocumentMeta {
  kind?: string;
  ext?: string | null;
  duplicate_uploads?: DuplicateUpload[];
  markdown_escapes_removed?: number;
  no_text_layer?: boolean;
  /** Set once the vision model has read the file — the text is a transcription, not source bytes. */
  ocr?: {
    state: 'done' | 'failed';
    model?: string; at?: string; pages?: number; chars?: number;
    truncated_pages?: number; error?: string;
  };
  no_text_reason?: string;
  ocr_pending?: boolean;
  speakers?: string[];
  transcript_flavour?: string;
  utterance_count?: number;
  has_timestamps?: boolean;
  first_timestamp?: string | null;
  last_timestamp?: string | null;
  cue_count?: number;
  line_count?: number;
  pages?: number;
  /** PowerPoint decks: slides found, and how much text their runs actually carried. */
  slide_count?: number;
  slide_text_chars?: number;
  /** A zip named .pptx whose [Content_Types].xml is not presentationml — never OCR-able. */
  not_a_presentation?: boolean;
  links?: string[];
  title?: string;
  content_fingerprint?: string;
  effective_date_detected_from?: 'filename' | 'content' | null;
  effective_date_pattern?: string | null;
  near_duplicate_hint?: NearDuplicateHint;
  text_quality?: { chars: number; control_ratio: number; alnum_ratio: number; word_ratio: number; garbled: boolean };
  /** Email documents: every attachment name found in the mail, and the unsupported ones. */
  attachment_names?: string[];
  skipped_attachments?: string[];
  /** Fanned-out attachments: the mail document they came from, and their name inside it. */
  from_email?: string;
  attachment_filename?: string;
  /** Set by POST /:id/rescan-attachments — the last re-scan's summary (P4: recorded). */
  last_attachment_rescan?: {
    at: string; by_user_id: number | null;
    created: number; linked_existing: number; already_present: number;
    still_unsupported: string[];
  };
  [key: string]: unknown;
}

/** The fixed folder vocabulary — a document's FORM, exactly one (librarian-classified). */
export const DOCUMENT_FOLDERS = [
  'correspondence', 'meeting', 'agreement', 'financial', 'specification',
  'presentation', 'image', 'note', 'deliverable', 'reference',
] as const;
export type DocumentFolder = (typeof DOCUMENT_FOLDERS)[number];

/** One engagement a client-owned document is LINKED to (engagement_documents row). */
export interface DocumentEngagementLink {
  id: number;
  code: string;
  origin: 'uploaded_here' | 'linked_from_library';
  note: string | null;
  published: boolean;
  linked_at: string;
  linked_by?: number | null;
}

export interface DocumentRow {
  id: number;
  /** Owner: documents belong to the CLIENT; engagements link a subset. */
  client_id: number | null;
  /** LEGACY upload location — no longer written for new rows; use the link data instead. */
  engagement_id: number | null;
  ref: string;
  sha256: string;
  original_filename: string;
  mime_type: string | null;
  byte_size: number | null;
  source_type: SourceType;
  effective_date: string | null;
  effective_date_confirmed: boolean;
  authored_by: string | null;
  status: DocStatus;
  superseded_by_document_id: number | null;
  superseded_by_ref?: string | null;
  text_format: TextFormat | null;
  extraction_error: string | null;
  /** LEGACY dead flag — portal visibility lives on the engagement link (link_published). */
  published: boolean;
  /** Librarian classification (operator-editable via PATCH). */
  folder: DocumentFolder | null;
  tags: string[];
  ai_summary: string | null;
  /**
   * ATTEST LAW 6. The three fields above are the librarian's PROPOSAL (◇) until the operator
   * edits them or holds them explicitly (▪). Reset to false by every re-classification.
   */
  classification_held?: boolean;
  meta: DocumentMeta | null;
  ingested_at: string;
  text_length: number;
  has_text: boolean;
  /** Present on GET /documents?engagement_id= rows — the LINK's own fields. */
  link_origin?: 'uploaded_here' | 'linked_from_library';
  link_note?: string | null;
  linked_at?: string;
  link_published?: boolean;
  /**
   * Server-derived: the near-duplicate hint has been ACTED on — explicitly dismissed, or a
   * supersession edge now exists between this document and one of the hinted candidates.
   * Not a column: a projection over meta + the supersession graph.
   */
  near_duplicate_resolved?: boolean;
  /**
   * Server-derived INBOUND view: the documents whose own hint names THIS one as a candidate.
   * A hint is written on one half of a pair only, so without this the other half has no sign
   * the question exists. Never persisted, never duplicated (P4) — projected.
   */
  near_duplicate_of?: InboundNearDuplicate[];

  // ── YIELD (GET /documents?engagement_id= only) ────────────────────────────
  // What this document has actually given THIS engagement's register. "28 documents ingested"
  // is an output; this is the outcome (Lean UX p.34) — and the only way to state the dark set.
  /** Spans of this document quoted as evidence by requirements of the engagement asked for. */
  cited_by_evidence?: number;
  /** Distinct requirements of that engagement citing it. */
  cited_by_requirements?: number;
  /** Has any extraction run of that engagement ever had it in `docs_covered`? */
  ever_covered?: boolean | null;
}

/** One document that names THIS one as a near-duplicate candidate. */
export interface InboundNearDuplicate {
  id: number;
  ref: string;
  kind: string;
  reason: string;
}

/** One span of a document quoted as evidence, with the requirement that cites it. */
export interface DocumentEvidence {
  id: number;
  quoted_span: string;
  locator: { char_start?: number; char_end?: number; [key: string]: unknown } | null;
  confidence: number | null;
  requirement_id: number;
  requirement_ref: string;
  /** The citing requirement's claim mark: 'baselined'/'confirmed' read ▪, a draft reads ◇. */
  requirement_status: string;
  engagement_id: number;
  engagement_code: string;
  statement: string | null;
}

export interface DocumentLink {
  id: number;
  ref: string;
  original_filename: string;
  effective_date: string | null;
  status: DocStatus;
  depth?: number;
}

export interface DocumentDetail extends DocumentRow {
  /** First linked engagement's code (display shorthand); null when unlinked. */
  engagement_code: string | null;
  /** Every linked engagement's code. */
  engagement_codes: string[];
  /** The full link rows — a client-owned document can be linked to N engagements. */
  engagements: DocumentEngagementLink[];
  extracted_text: string | null;
  supersession_chain: DocumentLink[];
  supersedes: DocumentLink[];
  /** Same-CLIENT candidates for the "mark superseded by…" picker. */
  siblings: DocumentLink[];
  /** Every span the register has taken from this document — the panel's first section. */
  evidence: DocumentEvidence[];
  /** null when the caller named no engagement: coverage is a per-engagement fact. */
  ever_covered: boolean | null;
}

/** GET /documents/library row: a library document with its linked engagements. */
export interface LibraryDocumentRow extends DocumentRow {
  engagements: DocumentEngagementLink[];
  superseded_by_ref?: string | null;
  cited_by_evidence: number;
}

export interface LibraryResponse {
  client_id: number;
  client_name: string;
  count: number;
  rows: LibraryDocumentRow[];
}

export interface NearDuplicateHint {
  kind: 'same_content' | 'same_filename' | 'same_base_name' | 'similar_name';
  reason: string;
  similarity: number;
  candidates: DocumentLink[];
  /** Set once the operator dismissed the hint — the record stays, the badge stops (P4). */
  resolved_at?: string;
  resolved_by?: number | null;
  resolution?: string;
  note?: string | null;
}

export interface UploadOutcome {
  filename: string;
  status: 'created' | 'duplicate' | 'failed';
  document?: DocumentRow;
  existing_ref?: string;
  existing_document_id?: number;
  /** Duplicates: how many times these bytes have now been re-uploaded. */
  duplicate_count?: number;
  /** Duplicates: the bytes were already in the CLIENT library and the existing document
   *  has now been LINKED into this engagement (client-level dedup). */
  linked?: boolean;
  error?: string;
  notice?: string;
  hint?: NearDuplicateHint | null;
}

export interface UploadReport {
  engagement_id: number;
  created: number;
  duplicates: number;
  failed: number;
  results: UploadOutcome[];
}

/** One attachment's fate in a POST /:id/rescan-attachments pass. */
export interface RescanOutcome {
  filename: string;
  outcome: 'created' | 'already-present' | 'linked-existing' | 'still-unsupported' | 'failed';
  document_id?: number;
  ref?: string;
  notice?: string;
  error?: string;
}

export interface RescanReport {
  document_id: number;
  ref: string;
  outcomes: RescanOutcome[];
  created: number;
  linked_existing: number;
  already_present: number;
  /** What TODAY's formats still cannot read — stays recorded on the mail. */
  skipped_attachments: string[];
}

export interface SpanResult {
  document_id: number;
  ref: string;
  original_filename: string;
  text_format: TextFormat | null;
  total_length: number;
  start: number;
  end: number;
  context: number;
  span: string;
  before: string;
  after: string;
  context_start: number;
  context_end: number;
  line_start: number;
  line_end: number;
}

/**
 * A document cited as evidence cannot be deleted (P2) — the plan says so and names supersede
 * as the way out. `cited_by` turns "3 requirement revisions" into three refs the operator can
 * actually go and open.
 */
export interface DocumentDeletionPlan extends DeletionPlan {
  cited_by: Array<{ id: number; ref: string; statement: string | null }>;
  /** Every engagement this document is linked into — delete severs all of them. */
  linked_engagements?: Array<{ id: number; code: string }>;
}

export interface DocumentPatch {
  effective_date?: string | null;
  source_type?: SourceType;
  authored_by?: string | null;
  /** Librarian metadata — the operator always outranks the model. */
  folder?: DocumentFolder | null;
  tags?: string[];
  ai_summary?: string | null;
  /** "The librarian got it right and I stand behind it" — ◇ becomes ▪. */
  classification_held?: boolean;
}

/**
 * The shared bulk shape (UNIFIED-FUNCTION F7.1): what the act did, and what it would not do,
 * with the reason. A bulk verb never reports success for a row it silently skipped.
 */
export interface BulkOutcome<T> {
  done: T[];
  refused: Array<{ id: number; ref: string | null; reason: string }>;
}

// ── Operator language for files that never reach the ingest pipeline ────────
// multer's MIME screen drops an executable before the route sees it (it must not
// error, or one bad file would abort the whole batch), so the upload report simply
// has no row for that file. Silence is the one thing an ingest must never do: pair
// the request with the report and account for every file the operator chose.

const ACCEPTED_FOR_HUMANS = 'PDF, Word, text, Markdown, VTT/SRT, CSV, JSON, Excel, images and email (.eml)';
const ACCEPTED_EXTENSIONS = new Set([
  '.txt', '.md', '.markdown', '.json', '.csv', '.log', '.text',
  '.vtt', '.srt', '.docx', '.doc', '.pdf', '.xlsx', '.xls',
  '.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp', '.tif', '.tiff', '.eml',
]);
const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot).toLowerCase() : '';
};

/** Why a file the operator picked never became a document — in their words, not MIME. */
export function rejectionReason(file: File): string {
  const ext = extensionOf(file.name);
  const bare = ext.replace(/^\./, '');
  if (file.size > MAX_UPLOAD_BYTES) return `${file.name} is larger than the 50 MB limit.`;
  if (!ext) return `The file has no extension, so Signet cannot tell what it is — it takes ${ACCEPTED_FOR_HUMANS}.`;
  if (!ACCEPTED_EXTENSIONS.has(ext)) return `${bare} files are not accepted — Signet takes ${ACCEPTED_FOR_HUMANS}.`;
  return `${bare} files were rejected by the browser before upload — Signet takes ${ACCEPTED_FOR_HUMANS}.`;
}

export const documentsApi = {
  list: (engagementId: number, filters: { source_type?: string; status?: string; q?: string } = {}) => {
    const params = new URLSearchParams({ engagement_id: String(engagementId) });
    for (const [k, v] of Object.entries(filters)) if (v) params.set(k, v);
    return request<DocumentRow[]>(`/documents?${params.toString()}`);
  },

  /**
   * One document in full. Pass the engagement to scope the YIELD section: "cited" on the
   * Documents tab means cited by THIS engagement's register, and coverage
   * (`ever_covered`) is a per-engagement fact that cannot be answered without it.
   */
  get: (id: number, engagementId?: number) =>
    request<DocumentDetail>(
      `/documents/${id}${engagementId ? `?engagement_id=${engagementId}` : ''}`,
    ),

  /**
   * The whole CLIENT library, with folder/tag filters and each document's linked
   * engagements. Pass engagementId + linked:false for the "link from library" picker
   * (documents NOT yet linked to that engagement).
   */
  library: (
    clientId: number,
    filters: { folder?: string; tags?: string[]; q?: string; status?: string; source_type?: string;
               engagementId?: number; linked?: boolean } = {},
  ) => {
    const params = new URLSearchParams({ client_id: String(clientId) });
    if (filters.folder) params.set('folder', filters.folder);
    for (const t of filters.tags ?? []) params.append('tag', t);
    if (filters.q) params.set('q', filters.q);
    if (filters.status) params.set('status', filters.status);
    if (filters.source_type) params.set('source_type', filters.source_type);
    if (filters.engagementId) {
      params.set('engagement_id', String(filters.engagementId));
      params.set('linked', filters.linked === false ? '0' : '1');
    }
    return request<LibraryResponse>(`/documents/library?${params.toString()}`);
  },

  /** Link a library document into an engagement (operator-confirmed, same client only). */
  link: (id: number, engagementId: number, note?: string) =>
    request<{ ok: boolean; document: DocumentDetail; engagement_id: number; engagement_code: string }>(
      `/documents/${id}/link`,
      { method: 'POST', body: JSON.stringify({ engagement_id: engagementId, ...(note ? { note } : {}) }) },
    ),

  /** Detach from ONE engagement (409 while that engagement's revisions cite it). The document stays in the library. */
  unlink: (id: number, engagementId: number) =>
    request<{ ok: boolean; document_id: number; ref: string; engagement_id: number; engagement_code: string }>(
      `/documents/${id}/link?engagement_id=${engagementId}`, { method: 'DELETE' },
    ),

  uploadFiles: async (engagementId: number, files: File[]): Promise<UploadReport> => {
    const form = new FormData();
    form.append('engagement_id', String(engagementId));
    for (const f of files) form.append('files', f);
    const report = await upload<UploadReport>('/documents/upload', form);

    // Account for every chosen file: anything the server never reported on was screened
    // out before the route ran, and must still appear as a failed row with a reason.
    const reported = new Set(report.results.map((r) => r.filename));
    const missing = files.filter((f) => !reported.has(f.name));
    if (!missing.length) return report;
    return {
      ...report,
      failed: report.failed + missing.length,
      results: [...report.results, ...missing.map((f) => ({
        filename: f.name,
        status: 'failed' as const,
        error: rejectionReason(f),
      }))],
    };
  },

  createNote: (engagementId: number, title: string, bodyMd: string, effectiveDate?: string | null) =>
    request<DocumentRow>('/documents/note', {
      method: 'POST',
      body: JSON.stringify({
        engagement_id: engagementId, title, body_md: bodyMd,
        ...(effectiveDate ? { effective_date: effectiveDate } : {}),
      }),
    }),

  update: (id: number, patch: DocumentPatch) =>
    request<DocumentRow>(`/documents/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  confirmDate: (id: number) => request<DocumentRow>(`/documents/${id}/confirm-date`, { method: 'POST' }),

  /**
   * Confirm a SET of detected effective dates in one act. Replaces 25 requests and 25 full-list
   * refetches with 1 + 1; the rows the operator doubted stay out of the set and stay unconfirmed.
   */
  confirmDates: (engagementId: number, documentIds: number[]) =>
    request<BulkOutcome<{ id: number; ref: string; effective_date: string | null }>>(
      '/documents/confirm-dates',
      { method: 'POST', body: JSON.stringify({ engagement_id: engagementId, document_ids: documentIds }) },
    ),

  /** "I looked, they are different" — the hint keeps its reason, the badge stops. */
  dismissNearDuplicate: (id: number, note?: string) =>
    request<DocumentRow>(`/documents/${id}/dismiss-near-duplicate`, {
      method: 'POST',
      body: JSON.stringify(note ? { note } : {}),
    }),

  /** What deleting this document would take with it, and why it might refuse. */
  deletionPlan: (id: number) => request<DocumentDeletionPlan>(`/documents/${id}/deletion-plan`),

  /**
   * Delete: this document should never have been ingested. Supersede is the other disposal —
   * for a document that was real and has been overtaken — and it stays exactly as it was.
   */
  remove: (id: number) =>
    request<{ ok: boolean; deleted: number; ref: string; file_removed: boolean }>(
      `/documents/${id}`, { method: 'DELETE' },
    ),

  supersede: (id: number, supersededByDocumentId: number | null) =>
    request<DocumentRow>(`/documents/${id}/supersede`, {
      method: 'POST',
      body: JSON.stringify({ superseded_by_document_id: supersededByDocumentId }),
    }),

  span: (id: number, start: number, end: number, context = 400) =>
    request<SpanResult>(`/documents/${id}/span?start=${start}&end=${end}&context=${context}`),

  /** Operator-authed blob URL — the session cookie rides along on a normal navigation. */
  downloadUrl: (id: number) => `/api/documents/${id}/download`,

  /** Is the vision model up, and what is queued or being read right now. */
  ocrStatus: () =>
    request<{ available: boolean; reason: string | null; pending: Array<{ document_id: number; state: string }> }>(
      '/documents/ocr/status',
    ),

  /** Queue a document for reading. Returns immediately — the row fills in as the queue drains. */
  runOcr: (id: number) =>
    request<{ queued: boolean; document_id: number; replacing: boolean; depth: number }>(
      `/documents/${id}/ocr`, { method: 'POST' },
    ),

  /** Is the librarian's model up, and which documents are queued for classification. */
  librarianStatus: () =>
    request<{ available: boolean; reason: string | null; pending: Array<{ document_id: number; state: string }> }>(
      '/documents/librarian/status',
    ),

  /** Queue one document for (re-)classification — folder, tags, one-line summary. */
  classify: (id: number) =>
    request<{ queued: boolean; document_id: number; replacing: boolean; depth: number }>(
      `/documents/${id}/classify`, { method: 'POST' },
    ),

  /**
   * Re-parse a mail's stored .eml against TODAY's supported formats: attachments that
   * were unsupported at ingest are fanned out now; identical bytes already in the
   * library are linked, never duplicated; still-unsupported ones stay recorded.
   */
  rescanAttachments: (id: number) =>
    request<RescanReport>(`/documents/${id}/rescan-attachments`, { method: 'POST' }),
};

export default documentsApi;
